import { useFormContext, useWatch } from "react-hook-form";
import { DefaultFormSelectField } from "../../../common/components/forms/inputs/dropdown/DefaultFormSelectField";
import { DefaultInputField } from "../../../common/components/forms/inputs/InputField/DefaultInputField";
import { RenderWhen } from "../../../common/infrastructure/components/RenderWhen";
import { CurrencyType, PaymentTypes } from "../../../common/enums";
import { ISelectFieldModel } from "../../../common/components/forms/inputs/dropdown/models/selectFieldModel";

interface IEligibleSubscriptionOption extends ISelectFieldModel {
  remainingVisits?: number;
  type?: string;
  activeUntil?: string;
}

interface IPaymentMethodFormFieldsProps {
  /**
   * Field names this component should bind to in the parent form.
   * Lets EventArrivalConfirmation and the AddPaymentMethod dialog use
   * different schemas while still rendering the same controls.
   */
  fieldNames: {
    paymentTypeId: string;
    subscriptionId: string;
    price: string;
    currencyId: string;
  };
  paymentOptions: ISelectFieldModel[];
  subscriptions: IEligibleSubscriptionOption[];
  isService?: boolean;
  serviceMinPrice?: number;
  serviceMaxPrice?: number;
  /** Tooltip for the payment-method dropdown (page-specific copy). */
  paymentTypeTooltip?: string;
  /**
   * When true, the subscription dropdown is disabled if there is only one option —
   * the arrival page uses this to communicate "user has only one active subscription".
   */
  disableSingleSubscription?: boolean;
  /**
   * Called when the staff pick a payment method themselves (a re-pick of the
   * method already shown counts). Used by the deposit tender rules to tell a
   * deliberate choice apart from one the form made after a price overshoot.
   */
  onPaymentTypeUserSelect?: () => void;
}

/**
 * The shared payment-method selector used by both the arrival page and the
 * "add payment method" dialog. Renders:
 *   • Payment-type dropdown (always)
 *   • Subscription dropdown (when Plan is chosen)
 *   • Price input in EUR (when a custom-price method is chosen — Cash, Card,
 *     Bank, Revolut, POS cash, MyPos; not Plan / Multisport / CoolFit which derive
 *     the price server-side)
 *
 * The parent form owns the schema and the submit; this component only renders
 * the controls and watches the selected payment type to flip between sub-fields.
 */
export const PaymentMethodFormFields = ({
  fieldNames,
  paymentOptions,
  subscriptions,
  isService,
  serviceMinPrice,
  serviceMaxPrice,
  paymentTypeTooltip,
  disableSingleSubscription,
  onPaymentTypeUserSelect,
}: IPaymentMethodFormFieldsProps) => {
  const { control } = useFormContext();
  const selectedPaymentTypeId = useWatch({
    control,
    name: fieldNames.paymentTypeId,
  });

  const isSelectedPaymentPlan = selectedPaymentTypeId === PaymentTypes.Plan;
  const enableCustomPriceInput =
    !isSelectedPaymentPlan &&
    selectedPaymentTypeId !== PaymentTypes.Multisport &&
    selectedPaymentTypeId !== PaymentTypes.CoolFit;

  const userHasOnlyOneSubscription =
    !!subscriptions && subscriptions.length === 1;

  const priceLabel =
    isService &&
    serviceMinPrice !== undefined &&
    serviceMaxPrice !== undefined &&
    serviceMinPrice !== serviceMaxPrice
      ? `Цена (услугата е с диапазон € ${serviceMinPrice.toFixed(2)} - € ${serviceMaxPrice.toFixed(2)})`
      : "Цена";

  return (
    <>
      <DefaultFormSelectField
        name={fieldNames.paymentTypeId}
        options={paymentOptions}
        label="Начин на плащане"
        tooltip={paymentTypeTooltip}
        includeId={false}
        onUserSelect={onPaymentTypeUserSelect}
      />

      <RenderWhen condition={isSelectedPaymentPlan}>
        <DefaultFormSelectField
          name={fieldNames.subscriptionId}
          options={subscriptions}
          label="Абонамент за плащане"
          disabled={!!disableSingleSubscription && userHasOnlyOneSubscription}
          tooltip={
            disableSingleSubscription && userHasOnlyOneSubscription
              ? "Потребителят има само един активен абонамент."
              : ""
          }
        />
      </RenderWhen>

      <RenderWhen condition={enableCustomPriceInput}>
        <DefaultInputField
          name={fieldNames.price}
          currencyName={fieldNames.currencyId}
          placeholder={"20"}
          label={priceLabel}
          type="number"
          step={0.01}
          currency={CurrencyType.EUR}
        />
      </RenderWhen>
    </>
  );
};
